import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { OrganizationService } from '../../../shared/services/organization/organization.service';
import { SetupAttentionService } from '../../../shared/services/setup/setup-attention.service';

export const COMPANY_PROFILE_ROUTE = '/features/hr/company-profile/list';

interface ProfileField {
  key: string;
  label: string;
  value: string;
  required: boolean;
}

@Component({
  selector: 'app-company-profile',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule, FormsModule, RouterModule,
    ButtonModule, InputTextModule, ProgressBarModule, TagModule, ToastModule,
  ],
  providers: [MessageService],
  templateUrl: './company-profile.component.html',
  styleUrl: './company-profile.component.scss',
})
export class CompanyProfileComponent implements OnInit {
  private orgService = inject(OrganizationService);
  private setupAttention = inject(SetupAttentionService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private cdr = inject(ChangeDetectorRef);

  @ViewChild('fieldName') fieldName?: ElementRef<HTMLInputElement>;
  @ViewChild('fieldTin') fieldTin?: ElementRef<HTMLInputElement>;
  @ViewChild('fieldSss') fieldSss?: ElementRef<HTMLInputElement>;
  @ViewChild('fieldPh') fieldPh?: ElementRef<HTMLInputElement>;
  @ViewChild('fieldPagibig') fieldPagibig?: ElementRef<HTMLInputElement>;
  @ViewChild('fieldAddress') fieldAddress?: ElementRef<HTMLInputElement>;
  @ViewChild('fieldEmail') fieldEmail?: ElementRef<HTMLInputElement>;

  loading = false;
  saving = false;
  dirty = false;

  companyName = '';
  tin = '';
  tinBranchCode = '';
  address = '';
  sssNo = '';
  philHealthNo = '';
  pagIbigNo = '';
  pagIbigBranchCode = '';
  email = '';
  rdoCode = '';
  zipCode = '';

  returnUrl: string | null = null;

  get fromReport(): boolean {
    return !!this.returnUrl?.includes('/reports/workspace');
  }

  ngOnInit(): void {
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    void this.load();
  }

  async load(): Promise<void> {
    this.loading = true;
    this.cdr.markForCheck();
    try {
      const res = await firstValueFrom(this.orgService.getAgencyProfile());
      this.applyProfile(res.profile);
      this.dirty = false;
    } catch {
      this.messageService.add({
        severity: 'warn',
        summary: 'Could not load',
        detail: 'Company profile could not be loaded. Check that the API is running.',
      });
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  private applyProfile(p: {
    companyName?: string | null;
    tin?: string | null;
    tinBranchCode?: string | null;
    address?: string | null;
    sssNo?: string | null;
    philHealthNo?: string | null;
    pagIbigNo?: string | null;
    pagIbigBranchCode?: string | null;
    email?: string | null;
    rdoCode?: string | null;
    zipCode?: string | null;
  } | null | undefined): void {
    this.companyName = p?.companyName?.trim() ?? '';
    this.tin = p?.tin?.trim() ?? '';
    this.tinBranchCode = p?.tinBranchCode?.trim() ?? '';
    this.address = p?.address?.trim() ?? '';
    this.sssNo = p?.sssNo?.trim() ?? '';
    this.philHealthNo = p?.philHealthNo?.trim() ?? '';
    this.pagIbigNo = p?.pagIbigNo?.trim() ?? '';
    this.pagIbigBranchCode = p?.pagIbigBranchCode?.trim() ?? '';
    this.email = p?.email?.trim() ?? '';
    this.rdoCode = p?.rdoCode?.trim() ?? '';
    this.zipCode = p?.zipCode?.trim() ?? '';
  }

  markDirty(): void {
    if (!this.dirty) {
      this.dirty = true;
      this.cdr.markForCheck();
    }
  }

  get completionFields(): ProfileField[] {
    return [
      { key: 'name', label: 'Company name', value: this.companyName, required: true },
      { key: 'tin', label: 'BIR TIN', value: this.tin, required: true },
      { key: 'sss', label: 'SSS employer no.', value: this.sssNo, required: true },
      { key: 'ph', label: 'PhilHealth employer no.', value: this.philHealthNo, required: true },
      { key: 'pagibig', label: 'Pag-IBIG employer no.', value: this.pagIbigNo, required: true },
      { key: 'address', label: 'Registered address', value: this.address, required: false },
      { key: 'email', label: 'Email', value: this.email, required: false },
    ];
  }

  get completionPercent(): number {
    const required = this.completionFields.filter(f => f.required);
    const done = required.filter(f => f.value.trim()).length;
    return Math.round((done / required.length) * 100);
  }

  get isComplete(): boolean {
    return this.completionFields.filter(f => f.required).every(f => f.value.trim());
  }

  focusField(key: string): void {
    const map: Record<string, ElementRef<HTMLInputElement> | undefined> = {
      name: this.fieldName,
      tin: this.fieldTin,
      sss: this.fieldSss,
      ph: this.fieldPh,
      pagibig: this.fieldPagibig,
      address: this.fieldAddress,
      email: this.fieldEmail,
    };
    const el = map[key]?.nativeElement;
    el?.focus();
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  async save(): Promise<boolean> {
    const name = this.companyName.trim();
    if (!name) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Company name required',
        detail: 'Enter the legal or trade name shown on government forms.',
      });
      this.focusField('name');
      return false;
    }

    this.saving = true;
    this.cdr.markForCheck();
    try {
      const res = await firstValueFrom(
        this.orgService.upsertAgencyProfile({
          companyName: name,
          tin: this.tin.trim() || null,
          tinBranchCode: this.tinBranchCode.trim() || null,
          address: this.address.trim() || null,
          sssNo: this.sssNo.trim() || null,
          philHealthNo: this.philHealthNo.trim() || null,
          pagIbigNo: this.pagIbigNo.trim() || null,
          pagIbigBranchCode: this.pagIbigBranchCode.trim() || null,
          email: this.email.trim() || null,
          rdoCode: this.rdoCode.trim() || null,
          zipCode: this.zipCode.trim() || null,
        })
      );
      this.applyProfile(res.profile);
      this.dirty = false;
      this.setupAttention.refreshCompanyProfile();
      this.messageService.add({
        severity: 'success',
        summary: 'Saved',
        detail: 'Company profile saved. All government reports will use these details.',
      });
      return true;
    } catch (err: unknown) {
      const detail =
        (err as { error?: { message?: string } })?.error?.message ??
        'Could not save company profile.';
      this.messageService.add({ severity: 'error', summary: 'Save failed', detail });
      return false;
    } finally {
      this.saving = false;
      this.cdr.markForCheck();
    }
  }

  async saveAndReturn(): Promise<void> {
    const ok = await this.save();
    if (ok) this.back();
  }

  back(): void {
    if (this.returnUrl) {
      void this.router.navigateByUrl(this.returnUrl);
      return;
    }
    void this.router.navigate(['/features/hr/dashboard']);
  }
}
